import { Stack } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import ErrorBoundary from '../../../ErrorBoundary';
import { useStoredResult } from '../../../common/hooks/useStoredResult';
import { removeStorageKey, saveToStorage } from '../../../common/lib/storage';
import { ErrorTypes } from '../../../types/ErrorTypes';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';
import * as InnehavClient from '../../clients/innehavClient';
import { formErrorMap } from '../../lib/formErrorMap';
import { InnehavDto } from '../../model/ipm-innehav';
import { ForetagTransactions } from '../../types/ForetagTransactions';
import { ForetagAdditionalInfoRow } from '../ForetagAdditionalInfoRow/ForetagAdditionalInfoRow';
import { InnehavInfoSearchResult } from './InnehavInfoSearchResult';

interface ForetagInnehavProps {
  organisationsnummer: string;
  initialKundmarke?: string;
  companyName: string;
  permissions: ForetagTransactions[];
  cacheKey: string;
}

export const InnehavInfoContainer: React.FC<ForetagInnehavProps> = ({
  organisationsnummer,
  companyName,
  initialKundmarke,
  permissions = [],
  cacheKey,
}) => {
  const [error, setError] = useState<FormError>();
  const [kundmarke, setKundmarke] = useState('');

  const { loadState, searchResult, setLoadState, setSearchResult } =
    useStoredResult<InnehavDto[]>(cacheKey, organisationsnummer);

  useEffect(() => setKundmarke(initialKundmarke), [initialKundmarke]);

  const buyInnehav = async (
    e: FormEvent<HTMLFormElement>,
    organisationsnummer: string
  ) => {
    e.preventDefault();
    setLoadState(LoadState.Loading);
    setError(undefined);

    const response = await InnehavClient.getInnehav(
      organisationsnummer,
      kundmarke
    );

    if (response.success && response.data) {
      const innehav = response.data[0]?.innehav;
      setLoadState(LoadState.Success);
      if (innehav) {
        const result = innehav.sort((a, b) =>
          a.fastighetsbeteckning > b.fastighetsbeteckning ? 1 : -1
        );
        saveToStorage(cacheKey, result);
        setSearchResult(result);
      } else {
        saveToStorage(cacheKey, []);
      }
    } else {
      setLoadState(LoadState.Error);
      setError(
        {
          403: formErrorMap[ErrorTypes.Forbidden],
          451: formErrorMap[ErrorTypes.LegallyUnavailable],
          504: formErrorMap[ErrorTypes.GatewayTimeout],
        }[response.statusCode] || formErrorMap[ErrorTypes.UnknownError]
      );
    }
  };

  return (
    <ErrorBoundary onError={() => removeStorageKey(cacheKey)}>
      <form onSubmit={(e) => buyInnehav(e, organisationsnummer)}>
        <ForetagAdditionalInfoRow
          heading="Fastighetsinnehav"
          buyDisabled={!permissions.includes(ForetagTransactions.METORG01)}
          loadState={loadState}
          error={error}
          organisationsnummer={organisationsnummer}
          companyName={companyName}
          kundmarke={kundmarke}
          setKundmarke={setKundmarke}
          sourceName="Lantmäteriet"
        >
          <Stack direction="column">
            {loadState === LoadState.Success && (
              <InnehavInfoSearchResult innehav={searchResult} />
            )}
          </Stack>
        </ForetagAdditionalInfoRow>
      </form>
    </ErrorBoundary>
  );
};
